import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { OS, OS_LABELS, SchemaHelperWebComponentSchema, SchemaTable } from '../types';
import { GzAccordionWebComponent } from '../../../../ui/gzAccordion';
import { GzInputSearchWebComponent } from '../../../../ui/gzInput';
import { GzEnumFilterWebComponent } from '../../../../ui/gzEnumFilter';
import { HREF_LINK } from '../types';
import { LinkType } from 'apps/webcomponents/src/ui/gzLinkComponent/types';

export class SchemaHelperView extends BaseWebComponentView {
  private totalNumberOfTables: number | null = null;

  public dom: {
    $wrapper: HTMLElement;
    $inputSearch: GzInputSearchWebComponent;
    $osFilter: GzEnumFilterWebComponent;
    $tablesHeader: HTMLElement;
    $accordions: HTMLElement;
  };

  public getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$inputSearch = this.shadow.querySelector('#schema-helper-input-search');
    this.dom.$osFilter = this.shadow.querySelector('#schema-helper-os-filter');
    this.dom.$tablesHeader = this.shadow.querySelector('.tables-header');
    this.dom.$accordions = this.shadow.querySelector('#schema-helper-accordions');
  }

  public buildView(result: SchemaHelperWebComponentSchema): void {
    if (!result.success) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    const titleHeader: HTMLDivElement = this.shadow.querySelector('.title-header');

    titleHeader.innerHTML = `
      <div class="title-header-text">${this.dependencies.translateFn('Tables')}</div>
      <gz-link href="${HREF_LINK}" type="${
      LinkType.UNDERLINE
    }"><div class="title-header-link">${this.dependencies.translateFn('Learn more.')}</div></gz-link>
    `;

    this.dom.$inputSearch.setAttribute('placeholder', this.dependencies.translateFn('Search table'));

    this.dom.$osFilter.placeholder = this.dependencies.translateFn('Filter by: All platforms');
    this.dom.$osFilter.canSelectAll = true;
    this.dom.$osFilter.allItemsLabel = this.dependencies.translateFn('All platforms');
    this.dom.$osFilter.confirmActionButtonString = this.dependencies.translateFn('Apply');
    this.dom.$osFilter.filterOptions = [
      {
        label: OS_LABELS[OS.WINDOWS],
        value: OS.WINDOWS,
        selected: true,
      },
      {
        label: OS_LABELS[OS.LINUX],
        value: OS.LINUX,
        selected: true,
      },
      {
        label: OS_LABELS[OS.MAC],
        value: OS.MAC,
        selected: true,
      },
    ];

    this.totalNumberOfTables = result.data.length;

    this.renderSchemaHelper(result.data, '', []);
  }

  public renderSchemaHelper(
    result: Array<SchemaTable>,
    searchText: string,
    indexesOfOpenAccordions: Array<number>
  ): void {
    this.renderTablesHeader(result.length);

    if (0 === result.length) {
      this.dom.$accordions.innerHTML = `
        <div id="no-results-message" class="mt-2">${this.dependencies.translateFn('No results found')}</div>
      `;

      return;
    }

    this.dom.$accordions.innerHTML = '';

    result.forEach((element, index) => {
      const gzAccordion: GzAccordionWebComponent = document.createElement('gz-accordion') as GzAccordionWebComponent;
      gzAccordion.setAttribute('align', 'left');

      const highlightedText = this.getTextWithHighlight(element.name, searchText);

      if (indexesOfOpenAccordions.includes(index)) {
        gzAccordion.isOpen = true;
      }

      const columnsContentHTML = this.getColumnsContent(element.columns, searchText);
      const platformsContentHTML = this.getPlatformContent(element.platforms);

      gzAccordion.innerHTML += `
        <span slot="dynamic-title-content" class="title-text">
          ${highlightedText}
        </span>
        <div>
          <div class="platforms">
            <span class="platforms-images">${platformsContentHTML}</span>
            <span class="platforms-text">${this.dependencies.translateFn('Hover the lines for more info')}</span>
          </div>
          <div class="element-description">${element.description}</div>
          <div class="columns">${columnsContentHTML}</div>
        </div>
      `;

      this.dom.$accordions.append(gzAccordion);
    });
  }

  // returns the text that is bolded with matched searchText
  private getTextWithHighlight(text: string, searchText: string): string {
    if (searchText === '') {
      return text;
    }
    //remove special regex characters and replace spaces with | to create a regex or condition
    searchText = searchText.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '|');

    return text.replace(new RegExp('(' + searchText + ')', 'ig'), '<b>$1</b>');
  }

  private getPlatformContent(platforms: string[]): string {
    let platformsContentHTML = '';

    platforms.forEach(platform => {
      if ((<any>Object).values(OS).includes(platform)) {
        let title: string;

        title = OS_LABELS[platform];

        if (platform === OS.MAC) {
          platform = 'macos';
        }

        platformsContentHTML += `
          <gz-tooltip tooltipText="${title}">
            <img src="app/webcomponents/assets/images/icon-${platform}.svg" alt="${title}" />
          </gz-tooltip>
        `;
      }
    });

    return platformsContentHTML;
  }

  // search for all the words in the searchText to exist in textsToSearchIn (case-insensitive)
  public areAllWordsFromSearchTextIncludedInArray(textsToSearchIn: Array<string>, searchText: string): boolean {
    if (searchText === '') {
      return true;
    }
    searchText = searchText.toLowerCase();
    const filterWords = searchText.split(/\s+/);
    let foundWordInAnyText = false;
    for (const filterWord of filterWords) {
      foundWordInAnyText = false;
      for (const textToSearchIn of textsToSearchIn) {
        if (textToSearchIn.trim().toLowerCase().includes(filterWord)) {
          foundWordInAnyText = true;
          break;
        }
      }
      if (!foundWordInAnyText) {
        return false;
      }
    }

    return true;
  }

  private getColumnsContent(columns: SchemaTable['columns'], searchText: string): string {
    let columnsContentHTML = '';

    columns.forEach(column => {
      const highlightedText = this.getTextWithHighlight(column.name, searchText);

      columnsContentHTML += `
        <gz-tooltip tooltipText="${column.description}">
          <div class="columns-content">
            <div>
              <span class="column-text">${highlightedText}</span>
            </div>
            <div>
              <span class="column-type">${column.type}</span>
            </div>
          </div>
        </gz-tooltip>
      `;
    });

    return columnsContentHTML;
  }

  private renderTablesHeader(resultLength: number): void {
    const noOfTablesText = this.dependencies.translateFn('{resultLength} of {totalLength}', {
      resultLength: resultLength,
      totalLength: this.totalNumberOfTables,
    });

    this.dom.$tablesHeader.innerHTML = `
      <div><span class="tables-header-title">${this.dependencies.translateFn('TABLES')}</span></div>
      <div><span class="tables-header-nr-of-tables">${noOfTablesText}</span></div>
    `;
  }
}
